import { useMemo, useCallback, useState } from "react";
import {
  Stack,
  VStack,
  CircularProgress,
  List,
  ListItem,
  HStack,
  Box,
  Text,
  Image,
} from "@chakra-ui/react";

import Button from "components/Button/Button";
import Paginator from "components/Paginator/Paginator";

import { useGetSwapTransactionsQuery } from "services/swapApi";

import useCoinInfo from "hooks/useCoinInfo";

import isEmpty from "lodash/isEmpty";
import toLower from "lodash/toLower";
// import formatter from "util/formatter";
// import constants from "config/constant";
import toPrecision from "util/toPrecision";
import toCoinLocale from "util/toCoinLocale";
import { toQueryString } from "util/utils";
import { format, parseISO } from "date-fns";

function CoinAvatarFuse(props) {
  const { src, dest } = props;

  //   console.log("SRC-DEST", src, dest);

  const { getWalletInfo } = useCoinInfo();

  const getImage = useCallback(
    (coinName) => getWalletInfo(coinName)?.image,
    [getWalletInfo]
  );

  const srcImage = useMemo(() => getImage(src), [src, getImage]);
  const destImage = useMemo(() => getImage(dest), [dest, getImage]);

  //   console.log("IMAGES", getImage(src), getImage(dest));

  return (
    <HStack
      pos="relative"
      sx={{
        "--offset": "12px",
      }}
      h="32px"
      w="calc((32px * 2) - (var(--offset) / 2))"
    >
      <Box
        pos="absolute"
        top="50%"
        left="calc(50% - var(--offset))"
        transform="translate(-50%, -50%)"
        boxSize="32px"
        bg="white"
        shadow="0 0 3px rgba(0,0,0,.1)"
        borderRadius="50%"
        overflow="hidden"
      >
        <Image
          pos="absolute"
          top="50%"
          left="50%"
          boxSize="36px"
          transform="translate(-50%, -50%)"
          src={srcImage}
          objectFit="cover"
          alt=""
        />
      </Box>
      <Box
        pos="absolute"
        top="50%"
        right="calc(50% - var(--offset))"
        transform="translate(50%, -50%)"
        boxSize="32px"
        bg="white"
        shadow="0 0 3px rgba(0,0,0,.1)"
        borderRadius="50%"
        overflow="hidden"
      >
        <Image
          pos="absolute"
          top="50%"
          left="50%"
          boxSize="38px"
          transform="translate(-50%, -50%)"
          src={destImage}
          objectFit="cover"
          alt=""
        />
      </Box>
    </HStack>
  );
}

function TransactionItem(props) {
  const { src, dest, createdAt, srcAmount, destAmount, isLast } = props;
  // const { isOpen, onClose /*onOpen*/ } = useDisclosure();

  const getColor = (type) => {
    const map = {
      credit: "#00BA88",
      debit: "#ED2E7E",
    };

    return map[toLower(type) ?? "credit"];
  };

  //   const getType = (type) => {
  //     const map = {
  //       [toLower(type)]: toLower(type).includes("debit") ? "debit" : "credit",
  //       contract_credit: "credit",
  //       contract_debit: "debit",
  //     };

  //     return map[toLower(type)];
  //   };

  //   const isFiat = useMemo(() => constants.fiats?.includes(unit), [unit]);

  const Price = ({ type, value, coin }) => (
    <HStack m="0 !important" textTransform="initial">
      <Box boxSize="10px" borderRadius="50%" bg={getColor(type)} />
      <Text color="#4e4b66" fontSize="sm" fontWeight="400">
        {toPrecision(value, coin)}
      </Text>
    </HStack>
  );

  return (
    <>
      <ListItem
        w="100%"
        // minW="100%"
        as={Button}
        minH="fit-content"
        bg="transparent"
        // bg="#FCFCFC"
        p="32px 0"
        borderRadius="2px"
        borderBottom={!isLast ? "1px solid #EAEAEA4f" : "none"}
        _hover={{ bg: "transparent", shadow: "0 0 0 3px transparent" }}
        _active={{ bg: "transparent", shadow: "0 0 0 3px transparent" }}
        _focus={{ bg: "transparent", shadow: "0 0 0 3px transparent" }}
        cursor="pointer"
        // onClick={onOpen}
      >
        <HStack justifyContent="space-between" w="100%" px="4px">
          <HStack alignItems="flex-start">
            <CoinAvatarFuse src={src} dest={dest} />

            <Box maxW="180px" ml="18px !important">
              <Text
                textAlign="left"
                fontSize="14px"
                fontWeight="400"
                color="#4E4B66"
                w="100%"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
                textTransform="initial"
                m="0 !important"
              >
                {/* {description} */}
                {toCoinLocale(src ?? "celo")} to {toCoinLocale(dest ?? "btc")}{" "}
                Swap
              </Text>
              {createdAt && (
                <Text textAlign="left" fontSize="12px" mt="2px" color="#A0A3BD">
                  {format(parseISO(createdAt), "eee, dd MMM yyyy, HH:mm")}
                  {/* 2022-03-14 */}
                </Text>
              )}
            </Box>
          </HStack>

          {/* <Text color="#4E4B66" fontSize="14px" fontWeight="600">
            {toLower(type) === "debit" ? "-" : ""}
            {isFiat && formatter(unit).format(value?.$numberDecimal)}
            {!isFiat && toPrecision(value?.$numberDecimal, unit)}
          </Text> */}

          {srcAmount && destAmount && (
            <Stack justifyContent="flex-start" flex=".4" ml="0 !important">
              <Price type="credit" value={destAmount} coin={dest} />
              <Price type="debit" value={srcAmount} coin={src} />
            </Stack>
          )}
        </HStack>
      </ListItem>
    </>
  );
}

export default function HistoryPanel() {
  const pagination = {
    limit: 10,
    page: 1,
  };

  const [state, _setState] = useState(pagination);

  const setState = useCallback(
    (newState) => _setState(() => ({ ...state, ...newState })),
    [state]
  );

  const queries = useMemo(() => toQueryString(state), [state]);

  const { data, isLoading } = useGetSwapTransactionsQuery(queries);

  const txs = useMemo(() => data?.swap, [data]);
  const totalCount = useMemo(() => data?.totalCount, [data]);
  //   console.log("TXs", txs, totalCount);

  return (
    <>
      {isLoading && (
        <VStack my="40px">
          <CircularProgress
            ml="20px !important"
            isIndeterminate
            color="var(--primary)"
            size="20px"
          />
        </VStack>
      )}

      {/* {isEmp} */}

      {isEmpty(txs) && !isLoading && (
        <VStack my="40px">
          <Text>No swap transaction</Text>
        </VStack>
      )}

      {!isEmpty(txs) && (
        <>
          <List>
            {txs?.map((tx, i) => (
              <TransactionItem
                key={`swap-tx-${i}`}
                src={tx.sourceToken}
                dest={tx.destToken}
                srcAmount={tx.sourceQuantity.$numberDecimal}
                destAmount={tx.destQuantity.$numberDecimal}
                createdAt={tx.createdAt}
              />
            ))}
          </List>

          <Paginator
            {...state}
            totalCount={totalCount}
            onNextPage={(next) => setState({ page: next })}
            p="0"
            pt="20px"
          />
        </>
      )}
    </>
  );
}
